if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
        this.bindSheetSwipe();
      }

      isSheet() {
        return document.documentElement.dataset.quickAddStyle === 'sheet' && window.matchMedia('(max-width: 899px)').matches;
      }

      // On phones the dialog is a bottom sheet: dragging it down from the top of its scroll closes it.
      bindSheetSwipe() {
        const panel = this.querySelector('.quick-add-modal__content');
        if (!panel) return;
        let gesture = null;
        const reset = () => {
          panel.classList.remove('is-dragging');
          panel.style.removeProperty('transform');
          gesture = null;
        };
        panel.addEventListener(
          'touchstart',
          (event) => {
            gesture = null;
            if (!this.isSheet() || event.touches.length !== 1 || this.modalContent.scrollTop > 1) return;
            const touch = event.touches[0];
            gesture = { x: touch.clientX, y: touch.clientY, at: performance.now(), dy: 0, active: false };
          },
          { passive: true }
        );
        panel.addEventListener(
          'touchmove',
          (event) => {
            if (!gesture) return;
            if (event.touches.length !== 1) return reset();
            const dx = event.touches[0].clientX - gesture.x;
            const dy = event.touches[0].clientY - gesture.y;
            if (!gesture.active) {
              if ((Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) || dy < -8) return (gesture = null);
              if (dy < 8) return;
              if (this.modalContent.scrollTop > 1) return (gesture = null);
              gesture.active = true;
              panel.classList.add('is-dragging');
            }
            if (event.cancelable) event.preventDefault();
            gesture.dy = Math.max(0, dy);
            panel.style.transform = `translateY(${gesture.dy}px)`;
          },
          { passive: false }
        );
        panel.addEventListener(
          'touchend',
          () => {
            if (!gesture) return;
            const speed = gesture.dy / Math.max(1, performance.now() - gesture.at);
            const dismiss = gesture.active && (gesture.dy > 96 || (gesture.dy > 32 && speed > 0.6));
            reset();
            if (dismiss) this.hide();
          },
          { passive: true }
        );
        panel.addEventListener('touchcancel', reset, { passive: true });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        // The sheet slides down before it disappears, so keep its contents until the slide ends.
        if (this.isSheet()) {
          setTimeout(() => {
            if (!this.hasAttribute('open')) this.modalContent.innerHTML = '';
          }, 300);
        } else {
          this.modalContent.innerHTML = '';
        }

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (this.isSheet()) return this.showSheet(opener);
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // Phones open the sheet at once with the card's image and name and a spinner, then fill in the product form.
      showSheet(opener) {
        const token = (this.sheetToken = (this.sheetToken || 0) + 1);
        this.modalContent.replaceChildren(this.sheetPlaceholder(opener));
        super.show(opener);

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => {
            if (!response.ok) throw new Error(`${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (token !== this.sheetToken || !this.hasAttribute('open')) return;
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('product-info');

            this.preprocessHTML(productElement);
            this.reuseThumbnail(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
            trapFocus(this, this.querySelector('[role="dialog"]'));
          })
          .catch(() => {
            if (token !== this.sheetToken || !this.hasAttribute('open')) return;
            const status = this.modalContent.querySelector('.quick-add-sheet-loading__status');
            if (status) status.textContent = '読み込めませんでした。時間をおいてもう一度お試しください。';
          });
      }

      // The sheet thumbnail is 56px wide; showing the card image the placeholder already loaded avoids a second download and a flash.
      reuseThumbnail(productElement) {
        const loaded = this.modalContent.querySelector('.quick-add-sheet-loading__image')?.currentSrc;
        if (!loaded) return;
        const file = new URL(loaded).pathname.split('/').pop();
        const images = [...productElement.querySelectorAll('.product__media-wrapper .product__media-item img')];
        const thumb = images.find((image) => (image.getAttribute('src') || '').includes(file));
        if (!thumb) return;
        thumb.removeAttribute('srcset');
        thumb.removeAttribute('sizes');
        thumb.setAttribute('src', loaded);
        thumb.setAttribute('loading', 'eager');
      }

      sheetPlaceholder(opener) {
        const card = opener.closest('.card-wrapper') || opener.closest('.card') || opener.closest('.fumo-rail__item');
        const box = document.createElement('div');
        box.className = 'quick-add-sheet-loading';
        const head = document.createElement('div');
        head.className = 'quick-add-sheet-loading__head';
        const image = card?.querySelector('.card__media img, .fumo-rail__image');
        if (image) {
          const thumb = image.cloneNode(false);
          thumb.className = 'quick-add-sheet-loading__image';
          thumb.setAttribute('sizes', '56px');
          thumb.removeAttribute('loading');
          head.append(thumb);
        }
        const title = document.createElement('p');
        title.className = 'quick-add-sheet-loading__title';
        const link = card?.querySelector('.card__heading a');
        if (link) title.append(...[...link.childNodes].map((node) => node.cloneNode(true)));
        const railName = card?.querySelector('.fumo-rail__name');
        if (!link && railName) {
          title.append(railName.textContent);
          const number = card.querySelector('.fumo-rail__number')?.textContent;
          if (number) {
            const sub = document.createElement('span');
            sub.className = 'product-title-sub';
            sub.textContent = number;
            title.append(document.createElement('br'), sub);
          }
        }
        head.append(title);
        const status = document.createElement('div');
        status.className = 'quick-add-sheet-loading__status';
        status.setAttribute('role', 'status');
        const spinner = opener.querySelector('.loading__spinner svg');
        if (spinner) status.append(spinner.cloneNode(true));
        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = '読み込み中';
        status.append(label);
        box.append(head, status);
        return box;
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        if (this.isSheet()) this.moveLimitNote(productElement);
      }

      // In the phone sheet the purchase limit sits under the stock count, to the right of the quantity box.
      moveLimitNote(productElement) {
        const limit = productElement.querySelector('.product-limit-text');
        const inventory = productElement.querySelector('.product__inventory');
        if (!limit || !inventory) return;
        const count = limit.textContent.match(/(\d+)\s*点/);
        const note = document.createElement('span');
        note.className = 'quick-add-limit';
        note.textContent = count ? `お1人様${count[1]}点まで` : limit.textContent.trim();
        inventory.append(note);
        limit.remove();
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
